import {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {
  Box,
  Divider,
  Link,
  Spinner,
  Text,
  useBreakpointValue,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {NotImplemented} from '@amblique/contentful/app/components/contentful/not-implemented';
import {Markup} from '@amblique/base/app/components/shared/markup';
import {injectEmbedPanelScripts} from '@amblique/king-living/app/utils/embed-panel-scripts';

const IFRAME_LOAD_TIMEOUT_MS = 5000;
const IFRAME_FALLBACK_HEIGHT = '800px';
const IFRAME_SANDBOX = 'allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox';

/**
 * @override
 * EmbedPanel renders an iframe or HTML/script content from Contentful.
 * - Validates the iframe URL via fetch before rendering (browsers fire onLoad
 *   even for DNS-failure error pages, so fetch is the reliable error signal).
 * - On mobile, uses mobileHeightIframe → height → fallback for iframe height.
 * - Shows a loading spinner while the URL is being validated / the iframe loads.
 * - Shows an error message when the URL is unreachable or times out.
 * - Script Content (DE-795) runs as blob: URLs so CSP without 'unsafe-inline' still executes it.
 *
 * @param {Object} props
 * @param {Contentful.EmbedPanel} props.component
 * @param {boolean} [props.isComplete]
 * @param {boolean} [props.isFetching]
 */
export const EmbedPanel = ({component, isComplete = true, isFetching = false, ...props}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('contentful/embed-panel', {component, isComplete, isFetching});
  const scriptContainerRef = useRef(null);

  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);

  const {iframeUrl, htmlContent, scriptContent, height, mobileHeightIframe, separator} = component || {};

  const isMobile = useBreakpointValue({base: true, lg: false});
  const iframeHeight = isMobile ?
    mobileHeightIframe || height || IFRAME_FALLBACK_HEIGHT :
    height || IFRAME_FALLBACK_HEIGHT;

  // Pre-validate URL reachability before rendering the iframe.
  // fetch() with no-cors throws immediately for DNS / connection failures,
  // and the AbortController handles slow / unresponsive hosts.
  useEffect(() => {
    if (!iframeUrl || isFetching) return;

    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), IFRAME_LOAD_TIMEOUT_MS);

    setIframeLoading(true);
    setIframeError(false);

    fetch(iframeUrl, {mode: 'no-cors', signal: controller.signal})
      .then(() => clearTimeout(timeoutId))
      .catch(() => {
        if (cancelled) return;
        clearTimeout(timeoutId);
        setIframeLoading(false);
        setIframeError(true);
      });

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [iframeUrl, isFetching]);

  // DE-795: execute Script Content after mount. Inline bodies load as blob: URLs
  // because CSP script-src has no 'unsafe-inline'. innerHTML / textContent is a no-op.
  useEffect(() => {
    const container = scriptContainerRef.current;

    if (!scriptContent || !container) return undefined;

    const revokeBlobUrls = injectEmbedPanelScripts(container, scriptContent);

    return () => {
      revokeBlobUrls();
      container.replaceChildren();
    };
  }, [scriptContent]);

  // Hides the spinner once the iframe finishes loading.
  const handleIframeLoad = useCallback(() => setIframeLoading(false), []);

  // Fallback for any explicit iframe errors not caught by the fetch pre-check.
  const handleIframeError = useCallback(() => {
    setIframeLoading(false);
    setIframeError(true);
  }, []);

  if (isFetching) {
    return <NotImplemented component={component} isComplete={isComplete} isFetching={isFetching} {...props} />;
  }

  // Script-only entries must still mount so injectEmbedPanelScripts can run (DE-795).
  if (!iframeUrl && !htmlContent && !scriptContent) {
    return null;
  }

  if (iframeError && iframeUrl) {
    return (
      <Box {...styles.errorContainer} {...props}>
        <Box {...styles.error} p={6} textAlign="center">
          <Text color="gray.700" fontSize="md" mb={3}>
            {intl.formatMessage({
              defaultMessage: 'Unable to load form. Please contact us directly.',
              id: 'embed_panel.error.unable_to_load',
            })}
          </Text>
          <Link color="blue.600" fontSize="lg" fontWeight="semibold" href="tel:1300-546-454">
            {intl.formatMessage({
              defaultMessage: '1300 KING LIVING',
              id: 'embed_panel.contact.phone_number',
            })}
          </Link>
        </Box>
      </Box>
    );
  }

  return (
    <Box {...styles.container} {...props}>
      {separator && <Divider {...styles.separator} />}

      {iframeUrl ? (
        <Box {...styles.iframeWrapper} position="relative">
          {iframeLoading && (
            <Box
              {...styles.loading}
              alignItems="center"
              display="flex"
              height="100%"
              justifyContent="center"
              left={0}
              position="absolute"
              top={0}
              width="100%"
            >
              <Spinner color="blue.500" size="lg" />
              <Text color="gray.600" ml={3}>
                {intl.formatMessage({
                  defaultMessage: 'Loading form...',
                  id: 'embed_panel.loading.loading_form',
                })}
              </Text>
            </Box>
          )}
          <Box
            {...styles.iframe}
            as="iframe"
            height={iframeHeight}
            loading="lazy"
            opacity={iframeLoading ? 0 : 1}
            sandbox={IFRAME_SANDBOX}
            src={iframeUrl}
            title={intl.formatMessage({defaultMessage: 'Embedded Form', id: 'embed_panel.iframe.title'})}
            transition="opacity 0.3s ease-in-out"
            width="100%"
            onError={handleIframeError}
            onLoad={handleIframeLoad}
          />
        </Box>
      ) : htmlContent ? (
        <Box {...styles.htmlContent}>
          <Markup source={htmlContent} />
        </Box>
      ) : null}

      {scriptContent && <Box ref={scriptContainerRef} display="none" />}
    </Box>
  );
};

EmbedPanel.displayName = 'EmbedPanel';
EmbedPanel.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
